import type {
  GoogleDriveAuthorizationStart,
  GoogleDriveQuotaView,
  GoogleDriveQuotaSettingsSave,
  GoogleDriveQuotaSettingsView,
  GoogleDriveIntegrationView,
  GoogleDriveStorageProbeView,
  GoogleDriveVideoProbeView,
  GoogleDriveClientFoldersView,
  GoogleDriveClientFolderCheckView,
  GoogleDriveClientFolderDiagnostic,
} from '@tosho/contracts';
import { readDriveClientFolderDiagnostic } from '@tosho/contracts';

export class GoogleDriveApiError extends Error {
  public constructor(
    public readonly code: string,
    public readonly diagnostic: GoogleDriveClientFolderDiagnostic | null = null,
  ) {
    super('La connexion Google Drive ne peut pas être modifiée.');
  }
}

async function request(
  method: string,
  path: string,
  csrfToken?: string,
  body?: unknown,
): Promise<Response> {
  const response = await fetch(`/api/integrations/google-drive${path}`, {
    method,
    credentials: 'same-origin',
    cache: 'no-store',
    headers: {
      ...(csrfToken === undefined ? {} : { 'X-CSRF-Token': csrfToken }),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  if (!response.ok) {
    const body: unknown = await response.json().catch(() => null);
    const code =
      typeof body === 'object' &&
      body !== null &&
      'code' in body &&
      typeof body.code === 'string' &&
      [
        'REAUTHENTICATION_REQUIRED',
        'DRIVE_QUOTA_SETTINGS_CONFLICT',
        'DRIVE_OWNER_CHANGE_REQUIRES_MIGRATION',
        'DRIVE_STORAGE_CHECK_BUSY',
        'DRIVE_VIDEO_CHECK_BUSY',
      ].includes(body.code)
        ? body.code
        : 'UNAVAILABLE';
    const diagnostic =
      method === 'POST' &&
      path.startsWith('/clients/') &&
      path.endsWith('/folders') &&
      typeof body === 'object' &&
      body !== null &&
      'code' in body &&
      body.code === 'DRIVE_CLIENT_FOLDERS_UNAVAILABLE' &&
      'diagnostic' in body
        ? readDriveClientFolderDiagnostic(body.diagnostic)
        : null;
    throw new GoogleDriveApiError(code, diagnostic);
  }
  return response;
}

export const googleDriveApi = {
  async readQuotaSettings(): Promise<GoogleDriveQuotaSettingsView | null> {
    return (await (
      await request('GET', '/quota-settings')
    ).json()) as GoogleDriveQuotaSettingsView | null;
  },
  async saveQuotaSettings(
    csrfToken: string,
    body: GoogleDriveQuotaSettingsSave,
  ): Promise<GoogleDriveQuotaSettingsView> {
    return (await (
      await request('PUT', '/quota-settings', csrfToken, body)
    ).json()) as GoogleDriveQuotaSettingsView;
  },
  async measureQuota(csrfToken: string): Promise<GoogleDriveQuotaView> {
    return (await (
      await request('POST', '/quota-check', csrfToken)
    ).json()) as GoogleDriveQuotaView;
  },
  async readClientFolders(
    this: void,
    clientId: string,
  ): Promise<GoogleDriveClientFolderCheckView | null> {
    return (await (
      await request('GET', `/clients/${encodeURIComponent(clientId)}/folders`)
    ).json()) as GoogleDriveClientFolderCheckView | null;
  },
  async prepareClientFolders(
    this: void,
    csrfToken: string,
    clientId: string,
  ): Promise<GoogleDriveClientFoldersView> {
    return (await (
      await request('POST', `/clients/${encodeURIComponent(clientId)}/folders`, csrfToken)
    ).json()) as GoogleDriveClientFoldersView;
  },
  async readVideoCheck(): Promise<GoogleDriveVideoProbeView | null> {
    return (await (
      await request('GET', '/video-check')
    ).json()) as GoogleDriveVideoProbeView | null;
  },
  async runVideoCheck(
    csrfToken: string,
    cleanup: boolean,
  ): Promise<GoogleDriveVideoProbeView | null> {
    return (await (
      await request(cleanup ? 'DELETE' : 'POST', '/video-check', csrfToken)
    ).json()) as GoogleDriveVideoProbeView | null;
  },
  async readStorageCheck(): Promise<GoogleDriveStorageProbeView | null> {
    return (await (
      await request('GET', '/storage-check')
    ).json()) as GoogleDriveStorageProbeView | null;
  },
  async runStorageCheck(csrfToken: string): Promise<GoogleDriveStorageProbeView | null> {
    return (await (
      await request('POST', '/storage-check', csrfToken)
    ).json()) as GoogleDriveStorageProbeView | null;
  },
  async read(): Promise<GoogleDriveIntegrationView> {
    return (await (await request('GET', '')).json()) as GoogleDriveIntegrationView;
  },
  async begin(csrfToken: string, backupMetadata = false): Promise<string> {
    const result = (await (
      await request(
        'POST',
        '/authorization',
        csrfToken,
        backupMetadata ? { backupMetadata: true } : undefined,
      )
    ).json()) as GoogleDriveAuthorizationStart;
    const url = new URL(result.authorizationUrl);
    if (
      url.origin !== 'https://accounts.google.com' ||
      url.pathname !== '/o/oauth2/v2/auth' ||
      url.username ||
      url.password ||
      url.hash
    )
      throw new GoogleDriveApiError('UNAVAILABLE');
    return url.href;
  },
  async complete(csrfToken: string): Promise<void> {
    await request('POST', '/authorization/complete', csrfToken);
  },
  async cancel(csrfToken: string): Promise<void> {
    await request('DELETE', '/authorization', csrfToken);
  },
  async disable(csrfToken: string): Promise<void> {
    await request('DELETE', '/connection', csrfToken);
  },
};
